// src/ui/fear-feedback/fear-curve.ts — Phase 6 Plan 06-07 fear feedback curve.
//
// Pure number-math drivers for the FearFeedbackLayer (D-13, D-14).
//
// Three exported functions, all read-only over a single `fear` value in [0,1]:
//   - fearToVignetteOpacity (the warm-cozy darkening overlay; D-13 calibration:
//     Rimworld cozy, NOT Hotline Miami horror — peak opacity is intentionally
//     low so the family can still see the world).
//   - fearToHeartbeatBpm    (drives Howl.rate on the heartbeat sample; UI-SPEC
//     §FearFeedbackLayer specifies a base BPM with rate-modulation so a single
//     sample can play at the full BPM curve without crossfade infrastructure).
//   - fearToShiverAmplitudePx (per-frame sprite delta; the GameScene
//     setPlayerShiver hook applies this on top of interpolated sim position
//     so the sim's authoritative position is never perturbed).
//
// IMPORTANT: this module imports number constants only. NO React, NO DOM, NO
// Howler — the functions are referentially transparent and trivially unit-
// testable from a pure jsdom shell. The vignette ramp boundary numbers are
// taken VERBATIM from UI-SPEC §Color vignette ramp (0.0-0.35 / 0.35-0.65 /
// 0.65-0.90 / 0.90-1.0) so any retune lands here in one place.
//
// SOURCE: 06-CONTEXT.md D-13 (subtle Rimworld-style continuous feedback);
//         06-UI-SPEC.md §FearFeedbackLayer; 06-PATTERNS.md lines 432-465.

import {
  P6_HEARTBEAT_BPM_NERVOUS,
  P6_HEARTBEAT_BPM_AFRAID,
  P6_HEARTBEAT_BPM_TERRIFIED,
  P6_VIGNETTE_PEAK_OPACITY,
  P6_SHIVER_AMPLITUDE_PX,
  P6_FEAR_THRESHOLD_NERVOUS,
  P6_FEAR_THRESHOLD_AFRAID,
  P6_FEAR_THRESHOLD_TERRIFIED,
} from '../../render/audio/constants';

/**
 * Vignette opacity ramp — 4-segment piecewise linear:
 *
 *   0.00..0.35  — 0 (transparent; below the nervous gate)
 *   0.35..0.65  — 0 → 0.12 linear (nervous segment)
 *   0.65..0.90  — 0.12 → 0.25 linear (afraid segment)
 *   0.90..1.00  — 0.25 → 0.35 linear, clamped at P6_VIGNETTE_PEAK_OPACITY
 *
 * Boundary values come straight from UI-SPEC §Color vignette ramp; the upper
 * clamp is defensive — if a future P4 fear curve emits values > 1.0 we still
 * cap at 0.35 so the Rimworld-cozy palette is preserved.
 */
export function fearToVignetteOpacity(fear: number): number {
  if (fear < P6_FEAR_THRESHOLD_NERVOUS) return 0;
  if (fear < P6_FEAR_THRESHOLD_AFRAID) {
    return (
      ((fear - P6_FEAR_THRESHOLD_NERVOUS) /
        (P6_FEAR_THRESHOLD_AFRAID - P6_FEAR_THRESHOLD_NERVOUS)) *
      0.12
    );
  }
  if (fear < P6_FEAR_THRESHOLD_TERRIFIED) {
    return (
      0.12 +
      ((fear - P6_FEAR_THRESHOLD_AFRAID) /
        (P6_FEAR_THRESHOLD_TERRIFIED - P6_FEAR_THRESHOLD_AFRAID)) *
        0.13
    );
  }
  return Math.min(
    P6_VIGNETTE_PEAK_OPACITY,
    0.25 +
      ((fear - P6_FEAR_THRESHOLD_TERRIFIED) /
        (1 - P6_FEAR_THRESHOLD_TERRIFIED)) *
        0.10,
  );
}

/**
 * Heartbeat BPM curve — flat plateau at NERVOUS for the nervous band, then
 * linear ramps through afraid → terrified peak. Sub-threshold idle is 60% of
 * NERVOUS (= 30 BPM) so the heartbeat is audible-but-quiet during normal play
 * (Rimworld cozy). The audio manager applies this via Howl.rate so one
 * recorded sample at P6_HEARTBEAT_BASE_BPM = 75 covers the full 30..100 range
 * with moderate pitch shift symmetric around the base (P6-Pitfall F).
 *
 *   0.00..0.35  — NERVOUS * 0.6 = 30 (sub-threshold idle, flat)
 *   0.35..0.65  — NERVOUS = 50 (flat plateau across nervous band)
 *   0.65..0.90  — NERVOUS → AFRAID linear (50 → 80)
 *   0.90..1.00  — AFRAID → TERRIFIED linear (80 → 100)
 */
export function fearToHeartbeatBpm(fear: number): number {
  if (fear < P6_FEAR_THRESHOLD_NERVOUS) return P6_HEARTBEAT_BPM_NERVOUS * 0.6;
  if (fear < P6_FEAR_THRESHOLD_AFRAID) return P6_HEARTBEAT_BPM_NERVOUS;
  if (fear < P6_FEAR_THRESHOLD_TERRIFIED) {
    return (
      P6_HEARTBEAT_BPM_NERVOUS +
      ((fear - P6_FEAR_THRESHOLD_AFRAID) /
        (P6_FEAR_THRESHOLD_TERRIFIED - P6_FEAR_THRESHOLD_AFRAID)) *
        (P6_HEARTBEAT_BPM_AFRAID - P6_HEARTBEAT_BPM_NERVOUS)
    );
  }
  return (
    P6_HEARTBEAT_BPM_AFRAID +
    ((fear - P6_FEAR_THRESHOLD_TERRIFIED) /
      (1 - P6_FEAR_THRESHOLD_TERRIFIED)) *
      (P6_HEARTBEAT_BPM_TERRIFIED - P6_HEARTBEAT_BPM_AFRAID)
  );
}

/**
 * Sprite shiver amplitude — zero below the nervous gate, then linear scaling
 * to P6_SHIVER_AMPLITUDE_PX (= 2 px) at fear=1.0. The 4 Hz oscillation is
 * applied at the consumer (FearFeedbackLayer's per-rAF Math.sin/cos pair on
 * (performance.now() / 1000) * P6_SHIVER_HZ * π) so this function is purely
 * the amplitude scalar.
 */
export function fearToShiverAmplitudePx(fear: number): number {
  if (fear < P6_FEAR_THRESHOLD_NERVOUS) return 0;
  return fear * P6_SHIVER_AMPLITUDE_PX;
}
